import { motion } from "framer-motion";
import { AppShell } from "@/components/AppShell";
import { BentoCard } from "@/components/BentoCard";
import { BuildEaseLogo } from "@/components/BuildEaseLogo";
import { HUDStatusBadge } from "@/components/HUDStatusBadge";
import { MaterialsBarChart } from "@/components/MaterialsBarChart";
import { formatZAR, useDashboardStore } from "@/store/dashboardStore";
import { selectMaterialsTotal, useMaterialsStore } from "@/store/materialsStore";
import { selectActiveProject, useProjectStore } from "@/store/projectStore";

const EASE = [0.22, 1, 0.36, 1] as const;

const card = {
  hidden: { opacity: 0, y: 20 },
  show: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: 0.15 + i * 0.09, duration: 0.6, ease: EASE },
  }),
};

const Eyebrow = ({ children }: { children: React.ReactNode }) => (
  <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-cad">{children}</span>
);

export default function DashboardScreen() {
  const project = useProjectStore(selectActiveProject);
  const getForProject = useDashboardStore((s) => s.getForProject);
  const data = getForProject(project?.id ?? "prj_default");
  const estimatedTotal = useMaterialsStore(selectMaterialsTotal);
  const usedMaterials = data.materials.filter((m) => m.used > 0).length;

  return (
    <AppShell>
      <div data-testid="dashboard-screen" className="flex min-h-screen flex-col">
        <motion.header
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: EASE }}
          className="sticky top-0 z-30 flex items-center justify-between gap-4 border-b border-cad/20 bg-obsidian/80 px-5 py-4 backdrop-blur-xl md:px-10"
        >
          <div className="flex min-w-0 items-center gap-4">
            <BuildEaseLogo height={28} className="md:hidden" />
            <span aria-hidden className="hidden h-6 w-px bg-cad/30 md:block" />
            <div className="flex min-w-0 flex-col">
              <Eyebrow>Project</Eyebrow>
              <h1
                data-testid="dashboard-project-name"
                className="truncate font-display text-base font-semibold tracking-tight text-white md:text-lg"
              >
                {project?.name ?? "No project yet"}
              </h1>
            </div>
          </div>
          <HUDStatusBadge status="ONLINE" tone="online" />
        </motion.header>

        <motion.section
          initial="hidden"
          animate="show"
          className="grid grid-cols-1 gap-4 px-5 py-6 md:grid-cols-6 md:gap-5 md:px-10 md:py-10"
        >
          <motion.div variants={card} custom={0} className="md:col-span-4 md:row-span-2">
            <BentoCard highlight data-testid="card-live-processing" className="flex h-full min-h-[260px] flex-col justify-between p-6 md:p-8">
              <span
                aria-hidden
                className="animate-scanline pointer-events-none absolute inset-x-0 h-px bg-gradient-to-r from-transparent via-safety/70 to-transparent"
              />
              <div className="flex items-start justify-between gap-4">
                <div className="flex flex-col gap-1.5">
                  <Eyebrow>{data.processing.stage}</Eyebrow>
                  <h2 className="font-display text-2xl font-semibold tracking-tight text-white sm:text-3xl">
                    {data.processing.level}
                  </h2>
                </div>
                <span className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-safety">
                  <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-safety" />
                  Live
                </span>
              </div>
              <div className="flex flex-col gap-3">
                <div className="flex items-end justify-between">
                  <span className="font-mono text-xs uppercase tracking-[0.18em] text-white/60">Beams · Columns · Slabs</span>
                  <span data-testid="processing-progress" className="font-display text-3xl font-bold text-safety">
                    {Math.round(data.processing.progress * 100)}%
                  </span>
                </div>
                <div className="h-1.5 overflow-hidden rounded-full bg-cad/30">
                  <motion.div
                    initial={{ scaleX: 0 }}
                    animate={{ scaleX: data.processing.progress }}
                    transition={{ delay: 0.5, duration: 1.1, ease: EASE }}
                    style={{ originX: 0 }}
                    className="h-full rounded-full bg-safety"
                  />
                </div>
              </div>
            </BentoCard>
          </motion.div>

          <motion.div variants={card} custom={1} className="md:col-span-2">
            <BentoCard data-testid="card-estimated-total" className="flex h-full flex-col justify-between gap-6 p-6">
              <Eyebrow>Estimated Total</Eyebrow>
              <div className="flex flex-col gap-1">
                <span
                  data-testid="estimated-total-value"
                  className="font-display text-3xl font-bold leading-none tracking-tight text-safety sm:text-4xl"
                >
                  {formatZAR(Math.round(estimatedTotal))}
                </span>
                <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-cad">ZAR · incl. VAT</span>
              </div>
            </BentoCard>
          </motion.div>

          <motion.div variants={card} custom={2} className="md:col-span-2">
            <BentoCard data-testid="card-active-flags" className="flex h-full items-center justify-between gap-4 p-6">
              <div className="flex flex-col gap-1">
                <span data-testid="active-flags-value" className="font-display text-2xl font-semibold tracking-tight text-white">
                  {data.activeFlags} Active Flags
                </span>
                <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-cad">Needs review</span>
              </div>
              <span className="flex h-11 w-11 items-center justify-center rounded-full border border-safety/50 font-mono text-sm text-safety">
                !
              </span>
            </BentoCard>
          </motion.div>

          <motion.div variants={card} custom={3} className="md:col-span-6">
            <BentoCard data-testid="card-materials" className="flex flex-col gap-6 p-6 md:p-8">
              <div className="flex items-start justify-between gap-4">
                <div className="flex flex-col gap-1">
                  <Eyebrow>Materials</Eyebrow>
                  <h2 className="font-display text-lg font-semibold tracking-tight text-white">
                    {usedMaterials} of {data.materials.length} streams in use
                  </h2>
                </div>
                <div className="flex items-center gap-4 font-mono text-[10px] uppercase tracking-[0.18em] text-cad">
                  <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-sm bg-safety" />Used</span>
                  <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-sm bg-cad/50" />Remaining</span>
                </div>
              </div>
              <MaterialsBarChart materials={data.materials} />
            </BentoCard>
          </motion.div>
        </motion.section>
      </div>
    </AppShell>
  );
}
